import type { TalkRoom } from "./types";
import { talkBrand } from "./brand";
import { ui, uiLocale } from "./ui";

interface LocalDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function localParts(value: string): LocalDateTimeParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) throw new Error(ui("Choose a valid date and time.", "Укажите корректные дату и время."));
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? 0),
  };
}

function partsAt(date: Date, timeZone: string): LocalDateTimeParts {
  const values = new Map(new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).map((part) => [part.type, part.value]));
  return {
    year: Number(values.get("year")),
    month: Number(values.get("month")),
    day: Number(values.get("day")),
    hour: Number(values.get("hour")),
    minute: Number(values.get("minute")),
    second: Number(values.get("second")),
  };
}

function asUTC(parts: LocalDateTimeParts): number {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

export function zonedDateTimeToUTC(value: string, timeZone: string): string {
  const target = localParts(value);
  const initial = asUTC(target);
  const firstOffset = asUTC(partsAt(new Date(initial), timeZone)) - initial;
  let resolved = initial - firstOffset;
  const secondOffset = asUTC(partsAt(new Date(resolved), timeZone)) - resolved;
  if (secondOffset !== firstOffset) resolved = initial - secondOffset;
  const roundTrip = partsAt(new Date(resolved), timeZone);
  if (roundTrip.year !== target.year || roundTrip.month !== target.month || roundTrip.day !== target.day || roundTrip.hour !== target.hour || roundTrip.minute !== target.minute) {
    throw new Error(ui("That local time does not exist in the selected time zone. Choose another time.", "Такого местного времени в выбранном часовом поясе нет. Выберите другое."));
  }
  return new Date(resolved).toISOString();
}

export function utcToZonedInput(value: string, timeZone: string): string {
  const parts = partsAt(new Date(value), timeZone);
  const pad = (number: number) => String(number).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

export function defaultScheduledInput(now = new Date()): string {
  const next = new Date(now.getTime() + 60 * 60 * 1_000);
  next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15, 0, 0);
  const pad = (number: number) => String(number).padStart(2, "0");
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T${pad(next.getHours())}:${pad(next.getMinutes())}`;
}

export function localDateTimeParts(value: string): { date: string; time: string } {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(value);
  return match ? { date: match[1]!, time: match[2]! } : { date: "", time: "" };
}

export function combineLocalDateTime(date: string, time: string): string {
  return date && /^\d{2}:\d{2}$/.test(time) ? `${date}T${time}` : "";
}

export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function formatZonedDateTime(value: string | Date, timeZone: string, locale = uiLocale()): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

export function formatZonedTime(value: string | Date, timeZone: string, locale = uiLocale()): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

export function formatMeetingTime(room: TalkRoom, locale = uiLocale()): string {
  if (!room.scheduledAt) return ui("Available now", "Уже можно войти");
  return new Intl.DateTimeFormat(locale, {
    timeZone: room.scheduledTimeZone || "UTC",
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  }).format(new Date(room.scheduledAt));
}

export function meetingDetails(room: TalkRoom, url: string, locale?: string): string {
  const lines = [room.title];
  if (room.scheduledAt) lines.push(`${ui("When", "Когда")}: ${formatMeetingTime(room, locale)}`);
  lines.push(`${ui("Join", "Ссылка")}: ${url}`);
  lines.push(room.allowGuests ? ui("Guests may join from the link.", "Гости могут войти по ссылке.") : ui(`A ${talkBrand.organizationName} account is required.`, `Нужен аккаунт ${talkBrand.organizationName}.`));
  return lines.join("\n");
}
